<template>
  <div class="dashboard-software-dev-view pa-4">
    <!-- 1. Header / Tarjeta de Título Neutra -->
    <v-card class="mb-4 rounded-xl border-light bg-white" outlined elevation="0">
      <v-card-title class="d-flex align-center justify-space-between py-4 px-5 flex-wrap">
        <div class="d-flex align-center">
          <v-avatar color="grey lighten-4" size="48" class="mr-3">
            <v-icon color="primary" large>mdi-chart-line-stacked</v-icon>
          </v-avatar>
          <div>
            <h1 class="text-h6 font-weight-black grey--text text--darken-4 mb-0">
              Dashboard de Desarrollo de Software
            </h1>
            <span class="text-caption grey--text text--darken-2">
              Gestión y seguimiento de requerimientos, desglose en historias de usuario y avance del ciclo ágil.
            </span>
          </div>
        </div>

        <div class="d-flex align-center flex-wrap mt-2 mt-md-0">
          <!-- Selector de Ámbito / Nivel de Visión -->
          <v-btn-toggle
            v-model="scopeMode"
            mandatory
            dense
            color="primary"
            class="mr-3 border-rounded elevation-0"
            @change="onScopeChange"
          >
            <v-btn value="my" small text class="font-weight-bold px-3 text-none">
              <v-icon small left>mdi-account-check</v-icon>
              Mis Requerimientos
            </v-btn>
            <v-btn value="gerencia" small text class="font-weight-bold px-3 text-none">
              <v-icon small left>mdi-domain</v-icon>
              Mi Gerencia / Área
            </v-btn>
            <v-btn value="all" small text class="font-weight-bold px-3 text-none">
              <v-icon small left>mdi-earth</v-icon>
              Visión Corporativa
            </v-btn>
          </v-btn-toggle>

          <v-btn
            color="primary"
            class="rounded-lg text-none font-weight-bold px-4"
            small
            elevation="0"
            :loading="loading"
            @click="loadDashboardData"
          >
            <v-icon left small>mdi-refresh</v-icon>
            Actualizar
          </v-btn>
        </div>
      </v-card-title>
    </v-card>

    <!-- Indicador de contexto de ámbito actual -->
    <v-card class="mb-4 rounded-lg border-light bg-white px-4 py-2" outlined elevation="0">
      <div class="d-flex align-center justify-space-between flex-wrap caption">
        <div class="d-flex align-center">
          <v-icon small color="primary" class="mr-2">mdi-filter-check-outline</v-icon>
          <span class="grey--text text--darken-3">
            Ámbito actual:
            <strong class="primary--text font-weight-bold">{{ scopeTitle }}</strong>
            <span v-if="currentUser.GerenciaName" class="grey--text text--darken-1 ml-1">
              ({{ currentUser.GerenciaName }} - {{ currentUser.AreaName || 'Área TI' }})
            </span>
          </span>
        </div>
        <div class="grey--text text--darken-2">
          Colaborador: <strong>{{ currentUser.PersonName || currentUser.UsrName }}</strong>
        </div>
      </div>
    </v-card>

    <!-- 2. Tarjetas de KPIs Ejecutivos (6 Tarjetas Neutras con Borde) -->
    <v-row dense class="mb-4">
      <!-- KPI 1: Total Requerimientos -->
      <v-col cols="12" sm="6" md="2">
        <v-card class="rounded-xl pa-3 bg-white border-light fill-height" outlined elevation="0">
          <div class="d-flex align-center">
            <v-avatar color="blue lighten-5" size="42" class="mr-3">
              <v-icon color="blue darken-2">mdi-folder-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                Requerimientos
              </div>
              <div class="text-h5 font-weight-black grey--text text--darken-4">
                {{ kpis.TotalRequirements || 0 }}
              </div>
              <div class="text-caption grey--text">
                {{ kpis.TotalStories || 0 }} historias desglosadas
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- KPI 2: Total Historias de Usuario -->
      <v-col cols="12" sm="6" md="2">
        <v-card class="rounded-xl pa-3 bg-white border-light fill-height" outlined elevation="0">
          <div class="d-flex align-center">
            <v-avatar color="indigo lighten-5" size="42" class="mr-3">
              <v-icon color="indigo darken-2">mdi-cards-variant</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                Historias TI
              </div>
              <div class="text-h5 font-weight-black grey--text text--darken-4">
                {{ kpis.TotalStories || 0 }}
              </div>
              <div class="text-caption grey--text">
                {{ calculateRatio(kpis.TotalStories, kpis.TotalRequirements) }} por req. prom.
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- KPI 3: Avance Global Consolidado -->
      <v-col cols="12" sm="6" md="2">
        <v-card class="rounded-xl pa-3 bg-white border-light fill-height" outlined elevation="0">
          <div class="d-flex align-center">
            <v-avatar :color="getProgressColor(kpis.GlobalProgress) + ' lighten-5'" size="42" class="mr-3">
              <v-icon :color="getProgressColor(kpis.GlobalProgress)">mdi-chart-donut</v-icon>
            </v-avatar>
            <div style="width: 100%;">
              <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                Avance Global
              </div>
              <div class="text-h5 font-weight-black grey--text text--darken-4">
                {{ kpis.GlobalProgress || 0 }}%
              </div>
              <v-progress-linear
                :value="kpis.GlobalProgress || 0"
                height="6"
                :color="getProgressColor(kpis.GlobalProgress)"
                class="rounded-pill mt-1"
              ></v-progress-linear>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- KPI 4: Historias Activas / En Desarrollo -->
      <v-col cols="12" sm="6" md="2">
        <v-card class="rounded-xl pa-3 bg-white border-light fill-height" outlined elevation="0">
          <div class="d-flex align-center">
            <v-avatar color="teal lighten-5" size="42" class="mr-3">
              <v-icon color="teal darken-2">mdi-cog-sync</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                En Desarrollo / QA
              </div>
              <div class="text-h5 font-weight-black teal--text text--darken-3">
                {{ kpis.ActiveStories || 0 }}
              </div>
              <div class="text-caption grey--text">
                {{ kpis.ActiveStoryPoints || 0 }} pts en curso
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- KPI 5: En Producción (Entregadas) -->
      <v-col cols="12" sm="6" md="2">
        <v-card class="rounded-xl pa-3 bg-white border-light fill-height" outlined elevation="0">
          <div class="d-flex align-center">
            <v-avatar color="green lighten-5" size="42" class="mr-3">
              <v-icon color="green darken-2">mdi-check-decagram</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">
                En Producción
              </div>
              <div class="text-h5 font-weight-black green--text text--darken-3">
                {{ kpis.InProduction || 0 }}
              </div>
              <div class="text-caption grey--text">
                {{ kpis.CompletedStoryPoints || 0 }} pts desplegados
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- KPI 6: Casos Críticos / Bloqueados -->
      <v-col cols="12" sm="6" md="2">
        <v-card class="rounded-xl pa-3 bg-white border-light fill-height" outlined elevation="0">
          <div class="d-flex align-center">
            <v-avatar color="red lighten-5" size="42" class="mr-3">
              <v-icon color="red darken-2">mdi-alert-octagon-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption text-uppercase font-weight-bold red--text text--darken-3">
                Bloqueos / Alertas
              </div>
              <div class="text-h5 font-weight-black red--text text--darken-2">
                {{ kpis.BlockedRequirements || actionables.length || 0 }}
              </div>
              <div class="text-caption red--text text--darken-1 font-weight-bold">
                {{ kpis.OverdueRequirements || 0 }} fuera de fecha
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- 3. Tarjeta de Filtros y Búsqueda -->
    <v-card class="mb-4 rounded-xl border-light bg-white" outlined elevation="0">
      <v-card-title class="py-3 px-5 d-flex align-center flex-wrap">
        <v-icon color="grey darken-3" small class="mr-2">mdi-filter-variant</v-icon>
        <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
          Filtros y Búsqueda de Requerimientos
        </span>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="py-3 px-5">
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3">
            <s-text
              v-model="searchQuery"
              label="Buscar por código, título o solicitante..."
              clearable
              dense
              hide-details
            ></s-text>
          </v-col>

          <v-col cols="12" sm="4" md="3">
            <s-select
              v-model="filters.project"
              :items="projectOptions"
              label="Proyecto / Sistema"
              clearable
              dense
              hide-details
              @input="applyClientFilters"
            ></s-select>
          </v-col>

          <v-col v-if="scopeMode === 'all'" cols="12" sm="4" md="3">
            <s-select
              v-model="filters.gerencia"
              :items="gerenciaOptions"
              label="Gerencia Solicitante"
              clearable
              dense
              hide-details
              @input="applyClientFilters"
            ></s-select>
          </v-col>

          <v-col cols="12" sm="4" md="3">
            <s-select-definition
              v-model="filters.status"
              :def="1721"
              label="Estado General"
              clearable
              dense
              hide-details
              @input="applyClientFilters"
            ></s-select-definition>
          </v-col>
        </v-row>

        <!-- Filtros rápidos por botones / chips -->
        <div class="d-flex align-center flex-wrap mt-3 pt-2 border-light-top">
          <span class="caption grey--text text--darken-2 font-weight-bold mr-2">Filtro rápido:</span>
          <v-chip
            small
            filter
            :input-value="quickFilter === 'all'"
            class="mr-2 my-1"
            @click="setQuickFilter('all')"
          >
            Todos ({{ requirements.length }})
          </v-chip>
          <v-chip
            small
            filter
            color="teal"
            text-color="teal darken-4"
            outlined
            :input-value="quickFilter === 'active'"
            class="mr-2 my-1"
            @click="setQuickFilter('active')"
          >
            En Curso / Desarrollo
          </v-chip>
          <v-chip
            small
            filter
            color="green"
            text-color="green darken-4"
            outlined
            :input-value="quickFilter === 'completed'"
            class="mr-2 my-1"
            @click="setQuickFilter('completed')"
          >
            Completados (100%)
          </v-chip>
          <v-chip
            small
            filter
            color="red"
            text-color="red darken-4"
            outlined
            :input-value="quickFilter === 'blocked'"
            class="mr-2 my-1"
            @click="setQuickFilter('blocked')"
          >
            Con Bloqueos / Alertas
          </v-chip>

          <v-spacer></v-spacer>

          <v-btn text small color="primary" class="text-none font-weight-bold" @click="toggleExpandAll">
            <v-icon small left>{{ expandedAll ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            {{ expandedAll ? 'Contraer Todos' : 'Expandir Todas las Historias' }}
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- 4. Grilla Principal: Requerimientos con Avance y Desglose de Historias (1 a N) -->
    <v-card class="rounded-xl border-light bg-white pa-4 mb-4" outlined elevation="0">
      <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
        <div class="d-flex align-center">
          <v-icon color="grey darken-3" class="mr-2">mdi-format-list-checks</v-icon>
          <span class="text-subtitle-1 font-weight-black grey--text text--darken-4 text-uppercase">
            Requerimientos y Desglose de Historias de Usuario
          </span>
          <v-chip x-small color="grey lighten-3" class="ml-2 font-weight-bold grey--text text--darken-3">
            {{ filteredRequirements.length }} de {{ requirements.length }}
          </v-chip>
        </div>

        <span class="caption grey--text text--darken-2">
          Haga clic en la fila o en la flecha para ver las historias de cada requerimiento.
        </span>
      </div>

      <v-data-table
        :headers="requirementHeaders"
        :items="filteredRequirements"
        :loading="loading"
        :expanded.sync="expanded"
        show-expand
        item-key="ReqID"
        dense
        class="border-rounded elevation-0"
        :no-data-text="'No se encontraron requerimientos para el ámbito o filtros seleccionados.'"
      >
        <!-- Columna Código -->
        <template v-slot:item.ReqCode="{ item }">
          <div class="d-flex align-center">
            <span class="font-weight-bold primary--text">{{ item.ReqCode }}</span>
            <v-tooltip v-if="item.ReqInitialPriorityName" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  x-small
                  :color="getPriorityColor(item.ReqInitialPriority)"
                  class="ml-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-flag
                </v-icon>
              </template>
              <span>Prioridad: {{ item.ReqInitialPriorityName }}</span>
            </v-tooltip>
          </div>
        </template>

        <!-- Columna Título y Proyecto -->
        <template v-slot:item.ReqTitle="{ item }">
          <div class="py-1">
            <div class="font-weight-bold grey--text text--darken-4 text-truncate" style="max-width: 320px;" :title="item.ReqTitle">
              {{ item.ReqTitle }}
            </div>
            <div class="caption grey--text text--darken-2 d-flex align-center flex-wrap">
              <v-chip x-small outlined color="grey darken-1" class="mr-1 my-0">
                <v-icon x-small left>mdi-folder-text-outline</v-icon>
                {{ item.ReqProject || 'Sin Proyecto' }}
              </v-chip>
              <span class="text-caption font-italic">{{ formatDate(item.ReqDate) }}</span>
            </div>
          </div>
        </template>

        <!-- Columna Solicitante / Gerencia -->
        <template v-slot:item.RequesterName="{ item }">
          <div class="caption">
            <div class="font-weight-bold grey--text text--darken-3">{{ item.RequesterName }}</div>
            <div class="grey--text text-truncate" style="max-width: 180px;">{{ item.GerenciaName }}</div>
          </div>
        </template>

        <!-- Columna Historias Desglosadas (Contador) -->
        <template v-slot:item.StoryCount="{ item }">
          <v-chip
            small
            :color="item.StoryCount > 0 ? 'indigo lighten-5' : 'grey lighten-4'"
            :text-color="item.StoryCount > 0 ? 'indigo darken-3' : 'grey darken-2'"
            class="font-weight-bold"
          >
            <v-icon x-small left :color="item.StoryCount > 0 ? 'indigo' : 'grey'">
              mdi-cards-outline
            </v-icon>
            {{ item.StoryCount }} {{ item.StoryCount === 1 ? 'historia' : 'historias' }}
          </v-chip>
        </template>

        <!-- Columna Avance General Ponderado -->
        <template v-slot:item.ProgressPercent="{ item }">
          <div class="py-1" style="min-width: 140px;">
            <div class="d-flex justify-space-between caption font-weight-bold mb-1">
              <span :class="getProgressTextColor(item.ProgressPercent)">
                {{ item.ProgressPercent }}%
              </span>
              <span class="grey--text caption font-weight-regular">
                {{ item.StoriesDoneCount || 0 }}/{{ item.StoryCount || 0 }} completadas
              </span>
            </div>
            <v-progress-linear
              :value="item.ProgressPercent"
              height="8"
              :color="getProgressColor(item.ProgressPercent)"
              class="rounded-pill"
            ></v-progress-linear>
          </div>
        </template>

        <!-- Columna Estado del Requerimiento -->
        <template v-slot:item.ReqStatusName="{ item }">
          <v-chip
            small
            :color="getStatusColor(item.ReqStatus)"
            dark
            class="font-weight-bold"
          >
            {{ item.ReqStatusName || 'Nuevo' }}
          </v-chip>
        </template>

        <!-- Columna Semáforo / Alertas -->
        <template v-slot:item.Alerts="{ item }">
          <div class="d-flex justify-center">
            <v-tooltip v-if="item.HasBlockedStories" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip x-small color="red" dark class="font-weight-bold" v-bind="attrs" v-on="on">
                  <v-icon x-small left>mdi-lock</v-icon> BLOQUEO
                </v-chip>
              </template>
              <span>Tiene historias bloqueadas</span>
            </v-tooltip>

            <v-tooltip v-else-if="item.HasOverdueStories" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip x-small color="deep-orange" dark class="font-weight-bold" v-bind="attrs" v-on="on">
                  <v-icon x-small left>mdi-clock-alert</v-icon> VENCIDO
                </v-chip>
              </template>
              <span>Tiene historias fuera de fecha</span>
            </v-tooltip>

            <v-tooltip v-else-if="item.ProgressPercent === 100" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small color="green darken-2" v-bind="attrs" v-on="on">mdi-check-circle</v-icon>
              </template>
              <span>Requerimiento 100% entregado</span>
            </v-tooltip>

            <span v-else class="caption grey--text font-italic">En curso</span>
          </div>
        </template>

        <!-- 5. Slot Expandido: Desglose de Historias de Usuario de cada Requerimiento -->
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-3 bg-grey-lighten">
            <v-card outlined class="rounded-lg border-light bg-white pa-3 elevation-0">
              <div class="d-flex align-center justify-space-between mb-2 pb-2 border-light-bottom flex-wrap">
                <div class="d-flex align-center">
                  <v-icon color="primary" small class="mr-2">mdi-layers-outline</v-icon>
                  <span class="subtitle-2 font-weight-black grey--text text--darken-4">
                    Desglose de Historias de Usuario ({{ (item.stories || []).length }}) para
                    <strong class="primary--text">{{ item.ReqCode }}</strong>:
                  </span>
                  <span class="caption grey--text text--darken-2 ml-2 font-italic text-truncate" style="max-width: 400px;">
                    "{{ item.ReqTitle }}"
                  </span>
                </div>

                <div class="d-flex align-center caption grey--text text--darken-2">
                  <span class="mr-3">
                    Puntos totales: <strong>{{ item.TotalStoryPoints || 0 }} pts</strong>
                  </span>
                  <span>
                    Completados: <strong>{{ item.CompletedStoryPoints || 0 }} pts</strong>
                  </span>
                </div>
              </div>

              <!-- Mensaje si no tiene historias creadas aún -->
              <v-alert
                v-if="!item.stories || item.stories.length === 0"
                type="info"
                text
                dense
                class="mb-0 caption"
                icon="mdi-information-outline"
              >
                Este requerimiento aún no ha sido desglosado en historias de usuario. El equipo de desarrollo TI se encuentra en la fase de refinamiento y estimación.
              </v-alert>

              <!-- Tabla de Historias del Requerimiento -->
              <v-data-table
                v-else
                :headers="storySubHeaders"
                :items="item.stories"
                dense
                hide-default-footer
                :items-per-page="-1"
                class="elevation-0 transparent border-light rounded-lg"
              >
                <!-- Código de la Historia -->
                <template v-slot:item.UsoCode="{ item: story }">
                  <span class="font-weight-bold indigo--text text--darken-3 caption">
                    {{ story.UsoCode }}
                  </span>
                </template>

                <!-- Título de la Historia -->
                <template v-slot:item.UsoTitle="{ item: story }">
                  <div class="caption font-weight-medium grey--text text--darken-4 py-1" style="max-width: 320px;">
                    {{ story.UsoTitle }}
                  </div>
                </template>

                <!-- Programador Asignado -->
                <template v-slot:item.AssignedITName="{ item: story }">
                  <div class="d-flex align-center caption">
                    <v-avatar size="22" color="indigo lighten-5" class="mr-1">
                      <v-icon x-small color="indigo darken-2">mdi-account-cog</v-icon>
                    </v-avatar>
                    <span class="font-weight-bold grey--text text--darken-3">
                      {{ story.AssignedITName || 'Sin asignar' }}
                    </span>
                  </div>
                </template>

                <!-- Sprint -->
                <template v-slot:item.SprintName="{ item: story }">
                  <v-chip x-small outlined color="grey darken-2" class="font-weight-bold">
                    <v-icon x-small left>mdi-run-fast</v-icon>
                    {{ story.SprintName || 'Backlog' }}
                  </v-chip>
                </template>

                <!-- Puntos de Historia -->
                <template v-slot:item.UsoStoryPoints="{ item: story }">
                  <v-chip
                    v-if="story.UsoStoryPoints"
                    x-small
                    color="deep-purple lighten-5"
                    text-color="deep-purple darken-3"
                    class="font-weight-bold"
                  >
                    {{ story.UsoStoryPoints }} pts ({{ story.UsoFinalSizeName || '-' }})
                  </v-chip>
                  <span v-else class="caption grey--text">Pendiente</span>
                </template>

                <!-- Estado de la Historia -->
                <template v-slot:item.UsoStatusName="{ item: story }">
                  <v-chip
                    x-small
                    :color="getStatusColor(story.UsoStatus)"
                    dark
                    class="font-weight-bold"
                  >
                    {{ story.UsoStatusName }}
                  </v-chip>
                </template>

                <!-- % de Avance de la Historia -->
                <template v-slot:item.StoryProgressPercent="{ item: story }">
                  <div class="d-flex align-center" style="min-width: 90px;">
                    <v-progress-linear
                      :value="story.StoryProgressPercent"
                      height="6"
                      :color="getProgressColor(story.StoryProgressPercent)"
                      class="rounded-pill mr-2"
                    ></v-progress-linear>
                    <span class="caption font-weight-bold" :class="getProgressTextColor(story.StoryProgressPercent)">
                      {{ story.StoryProgressPercent }}%
                    </span>
                  </div>
                </template>

                <!-- Fecha Compromiso / Alerta -->
                <template v-slot:item.UsoCommitmentDate="{ item: story }">
                  <div class="caption">
                    <div v-if="story.UsoCommitmentDate" class="grey--text text--darken-3">
                      {{ formatDate(story.UsoCommitmentDate) }}
                    </div>
                    <div v-else class="grey--text font-italic">Sin fecha</div>

                    <v-chip
                      v-if="story.UsoIsBlocked"
                      x-small
                      color="red"
                      dark
                      class="mt-1 font-weight-bold"
                    >
                      <v-icon x-small left>mdi-lock</v-icon> BLOQUEADO
                    </v-chip>
                    <v-chip
                      v-else-if="story.DeviationDays != null && story.DeviationDays < 0 && story.UsoStatus < 8"
                      x-small
                      color="deep-orange"
                      dark
                      class="mt-1 font-weight-bold"
                    >
                      <v-icon x-small left>mdi-clock-alert</v-icon> {{ Math.abs(story.DeviationDays) }}d retraso
                    </v-chip>
                  </div>
                </template>

                <!-- Motivo / Siguiente Acción en caso de Bloqueo -->
                <template v-slot:item.UsoNextAction="{ item: story }">
                  <div class="caption" style="max-width: 220px;">
                    <div v-if="story.UsoIsBlocked && story.UsoBlockReason" class="red--text text--darken-3 font-weight-bold mb-1">
                      Causa: {{ story.UsoBlockReason }}
                    </div>
                    <div v-if="story.UsoNextAction" class="grey--text text--darken-2 font-italic">
                      Plan: {{ story.UsoNextAction }}
                    </div>
                    <span v-if="!story.UsoIsBlocked && !story.UsoNextAction" class="grey--text">-</span>
                  </div>
                </template>
              </v-data-table>
            </v-card>
          </td>
        </template>
      </v-data-table>
    </v-card>

    <!-- 6. Secciones Analíticas: Distribución del Pipeline Ágil y por Proyecto -->
    <v-row class="mb-4">
      <!-- Distribución de Historias por Fase del Pipeline Ágil -->
      <v-col cols="12" md="6">
        <v-card outlined class="pa-4 rounded-xl border-light bg-white fill-height" elevation="0">
          <div class="subtitle-2 font-weight-black grey--text text--darken-4 text-uppercase mb-3 d-flex align-center justify-space-between">
            <span><v-icon color="primary" small class="mr-2">mdi-chart-timeline-variant</v-icon>Pipeline Ágil de Historias de Usuario</span>
            <v-chip x-small color="grey lighten-3" class="font-weight-bold grey--text text--darken-3">
              {{ kpis.TotalStories || 0 }} Historias
            </v-chip>
          </div>

          <div v-if="byStatus.length === 0" class="text-center py-4 grey--text caption">
            Sin datos de distribución
          </div>

          <div v-for="(item, idx) in byStatus" :key="'st-' + idx" class="mb-3">
            <div class="d-flex justify-space-between caption font-weight-bold mb-1">
              <span class="grey--text text--darken-3">{{ item.Name }}</span>
              <span>
                <strong>{{ item.Quantity }}</strong>
                <span class="grey--text">({{ calculatePercent(item.Quantity, kpis.TotalStories) }}%)</span>
              </span>
            </div>
            <v-progress-linear
              :color="getStatusColorByName(item.Name)"
              height="14"
              :value="calculatePercent(item.Quantity, kpis.TotalStories)"
              class="rounded-pill"
            ></v-progress-linear>
          </div>
        </v-card>
      </v-col>

      <!-- Avance por Proyecto / Sistema -->
      <v-col cols="12" md="6">
        <v-card outlined class="pa-4 rounded-xl border-light bg-white fill-height" elevation="0">
          <div class="subtitle-2 font-weight-black grey--text text--darken-4 text-uppercase mb-3 d-flex align-center justify-space-between">
            <span><v-icon color="primary" small class="mr-2">mdi-source-branch</v-icon>Avance por Proyecto / Sistema</span>
            <v-chip x-small color="grey lighten-3" class="font-weight-bold grey--text text--darken-3">
              {{ projectList.length }} Proyectos
            </v-chip>
          </div>

          <div v-if="projectList.length === 0" class="text-center py-4 grey--text caption">
            Sin datos de proyectos
          </div>

          <div v-for="(prj, idx) in projectList" :key="'prj-' + idx" class="mb-3">
            <div class="d-flex justify-space-between caption font-weight-bold mb-1">
              <span class="grey--text text--darken-3">{{ prj.name }}</span>
              <span>
                <strong>{{ prj.progress }}%</strong>
                <span class="grey--text ml-1">({{ prj.reqCount }} reqs / {{ prj.storyCount }} his)</span>
              </span>
            </div>
            <v-progress-linear
              :color="getProgressColor(prj.progress)"
              height="14"
              :value="prj.progress"
              class="rounded-pill"
            ></v-progress-linear>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- 7. Sección Inferior: Casos Críticos (Historias Bloqueadas o Vencidas) -->
    <v-card v-if="actionables.length > 0" outlined class="pa-4 rounded-xl border-light bg-white mb-4" elevation="0">
      <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
        <div class="d-flex align-center">
          <v-avatar color="red lighten-5" size="36" class="mr-2">
            <v-icon color="red darken-2" small>mdi-alert-decagram</v-icon>
          </v-avatar>
          <div>
            <h3 class="subtitle-2 font-weight-black red--text text--darken-3 mb-0 text-uppercase">
              Atención Inmediata: Historias Bloqueadas o Retrasadas
            </h3>
            <span class="caption grey--text text--darken-2">
              Seguimiento de impedimentos técnicos, dependencias y compromisos fuera de fecha
            </span>
          </div>
        </div>
        <v-chip color="red" dark class="font-weight-bold" small>
          {{ actionables.length }} Casos Críticos
        </v-chip>
      </div>

      <v-data-table
        :headers="actionableHeaders"
        :items="actionables"
        dense
        class="border-rounded elevation-0"
      >
        <template v-slot:item.ReqCode="{ item }">
          <span class="font-weight-bold primary--text caption">{{ item.ReqCode }}</span>
        </template>

        <template v-slot:item.UsoCode="{ item }">
          <span class="font-weight-bold indigo--text text--darken-3 caption">{{ item.UsoCode || '-' }}</span>
        </template>

        <template v-slot:item.StatusName="{ item }">
          <v-chip x-small color="grey darken-2" dark class="font-weight-bold">
            {{ item.StatusName }}
          </v-chip>
        </template>

        <template v-slot:item.AssignedITName="{ item }">
          <span class="caption font-weight-bold grey--text text--darken-3">
            {{ item.AssignedITName || 'Sin asignar' }}
          </span>
        </template>

        <template v-slot:item.AlertStatus="{ item }">
          <v-chip
            v-if="item.ReqIsBlocked"
            x-small
            color="red"
            dark
            class="font-weight-bold"
          >
            <v-icon x-small left>mdi-lock</v-icon> BLOQUEADO
          </v-chip>
          <v-chip
            v-else-if="item.DeviationDays != null && item.DeviationDays < 0"
            x-small
            color="deep-orange"
            dark
            class="font-weight-bold"
          >
            <v-icon x-small left>mdi-clock-alert</v-icon> {{ Math.abs(item.DeviationDays) }}d vencido
          </v-chip>
        </template>

        <template v-slot:item.BlockReason="{ item }">
          <div class="caption font-weight-bold red--text text--darken-3">
            {{ item.BlockReason || 'No especificada' }}
          </div>
        </template>

        <template v-slot:item.ReqNextAction="{ item }">
          <div class="caption font-italic grey--text text--darken-3">
            {{ item.ReqNextAction || 'Sin plan registrado' }}
          </div>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import _sRequirementService from "@/services/InformationTechnology/TicRequirementService.js";
import _sUserStoryService from "@/services/InformationTechnology/TicUserStoryService.js";
import { statusColor } from "./statusColors.js";

export default {
  name: "DashboardSoftwareDev",
  data() {
    return {
      loading: false,
      scopeMode: "my", // 'my', 'gerencia', 'all'
      currentUser: {
        UsrID: 0,
        UsrName: "",
        PersonName: "",
        AreaID: null,
        AreaName: "",
        GerenciaID: null,
        GerenciaName: ""
      },
      kpis: {
        TotalRequirements: 0,
        TotalStories: 0,
        ActiveRequirements: 0,
        ActiveStories: 0,
        BlockedRequirements: 0,
        InProduction: 0,
        ActiveStoryPoints: 0,
        CompletedStoryPoints: 0,
        OverdueRequirements: 0,
        GlobalProgress: 0
      },
      requirements: [],
      stories: [],
      byStatus: [],
      byGerencia: [],
      byProject: [],
      actionables: [],
      expanded: [],
      expandedAll: false,

      // Filtros
      searchQuery: "",
      quickFilter: "all", // 'all', 'active', 'completed', 'blocked'
      filters: {
        project: null,
        gerencia: null,
        status: null
      },

      // Encabezados de la grilla de requerimientos
      requirementHeaders: [
        { text: "Código", value: "ReqCode", width: "125px" },
        { text: "Requerimiento / Proyecto", value: "ReqTitle", width: "320px" },
        { text: "Solicitante / Gerencia", value: "RequesterName", width: "180px" },
        { text: "Historias", value: "StoryCount", width: "125px", align: "center" },
        { text: "Avance del Requerimiento", value: "ProgressPercent", width: "200px" },
        { text: "Estado General", value: "ReqStatusName", width: "150px", align: "center" },
        { text: "Alertas", value: "Alerts", width: "110px", align: "center" },
        { text: "", value: "data-table-expand" }
      ],

      // Encabezados del desglose de historias
      storySubHeaders: [
        { text: "Historia", value: "UsoCode", width: "115px" },
        { text: "Detalle de la Historia de Usuario", value: "UsoTitle" },
        { text: "Programador Asignado", value: "AssignedITName", width: "170px" },
        { text: "Sprint", value: "SprintName", width: "110px", align: "center" },
        { text: "Estimación", value: "UsoStoryPoints", width: "120px", align: "center" },
        { text: "Estado", value: "UsoStatusName", width: "140px", align: "center" },
        { text: "Avance", value: "StoryProgressPercent", width: "130px" },
        { text: "Compromiso", value: "UsoCommitmentDate", width: "125px" },
        { text: "Causa / Próxima Acción", value: "UsoNextAction", width: "200px" }
      ],

      // Encabezados de casos críticos
      actionableHeaders: [
        { text: "Req", value: "ReqCode", width: "120px" },
        { text: "Historia", value: "UsoCode", width: "120px" },
        { text: "Título", value: "UsoTitle" },
        { text: "Responsable TI", value: "AssignedITName", width: "160px" },
        { text: "Estado", value: "StatusName", width: "130px", align: "center" },
        { text: "Semáforo", value: "AlertStatus", width: "130px", align: "center" },
        { text: "Motivo Bloqueo", value: "BlockReason", width: "180px" },
        { text: "Plan de Acción", value: "ReqNextAction", width: "200px" }
      ]
    };
  },
  computed: {
    scopeTitle() {
      if (this.scopeMode === "my") return "Mis Solicitudes (Usuario)";
      if (this.scopeMode === "gerencia") return "Mi Gerencia / Área";
      return "Toda la Empresa (Corporativo)";
    },
    projectOptions() {
      const set = new Set();
      this.requirements.forEach(r => {
        if (r.ReqProject) set.add(r.ReqProject);
      });
      return Array.from(set).sort();
    },
    gerenciaOptions() {
      const set = new Set();
      this.requirements.forEach(r => {
        if (r.GerenciaName) set.add(r.GerenciaName);
      });
      return Array.from(set).sort();
    },
    filteredRequirements() {
      let list = this.requirements || [];

      // Filtro de ámbito si el backend no lo resolvió
      const currentUsrId = this.$fun.getUserID();
      if (this.scopeMode === "my") {
        list = list.filter(r => r.UsrCreateID === currentUsrId);
      } else if (this.scopeMode === "gerencia" && this.currentUser.GerenciaName) {
        list = list.filter(r => r.GerenciaName === this.currentUser.GerenciaName);
      }

      // Filtro de búsqueda de texto
      if (this.searchQuery && this.searchQuery.trim() !== "") {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter(r => {
          const code = (r.ReqCode || "").toLowerCase();
          const title = (r.ReqTitle || "").toLowerCase();
          const requester = (r.RequesterName || "").toLowerCase();
          const project = (r.ReqProject || "").toLowerCase();
          const matchStory = (r.stories || []).some(s =>
            (s.UsoCode || "").toLowerCase().includes(q) ||
            (s.UsoTitle || "").toLowerCase().includes(q) ||
            (s.AssignedITName || "").toLowerCase().includes(q)
          );
          return code.includes(q) || title.includes(q) || requester.includes(q) || project.includes(q) || matchStory;
        });
      }

      // Filtro por proyecto
      if (this.filters.project) {
        list = list.filter(r => r.ReqProject === this.filters.project);
      }

      // Filtro por gerencia
      if (this.filters.gerencia && this.scopeMode === "all") {
        list = list.filter(r => r.GerenciaName === this.filters.gerencia);
      }

      // Filtro por estado
      if (this.filters.status != null) {
        list = list.filter(r => r.ReqStatus === this.filters.status);
      }

      // Filtro rápido
      if (this.quickFilter === "active") {
        list = list.filter(r => r.ProgressPercent > 0 && r.ProgressPercent < 100);
      } else if (this.quickFilter === "completed") {
        list = list.filter(r => r.ProgressPercent === 100);
      } else if (this.quickFilter === "blocked") {
        list = list.filter(r => r.HasBlockedStories || r.HasOverdueStories);
      }

      return list;
    },
    projectList() {
      const map = {};
      this.filteredRequirements.forEach(r => {
        const prjName = r.ReqProject || "Sin Proyecto";
        if (!map[prjName]) {
          map[prjName] = { name: prjName, reqCount: 0, storyCount: 0, progressSum: 0 };
        }
        map[prjName].reqCount += 1;
        map[prjName].storyCount += r.StoryCount || 0;
        map[prjName].progressSum += r.ProgressPercent || 0;
      });

      return Object.values(map).map(p => ({
        name: p.name,
        reqCount: p.reqCount,
        storyCount: p.storyCount,
        progress: p.reqCount > 0 ? Number((p.progressSum / p.reqCount).toFixed(1)) : 0
      })).sort((a, b) => b.reqCount - a.reqCount);
    }
  },
  created() {
    this.loadCurrentUser();
    this.loadDashboardData();
  },
  methods: {
    loadCurrentUser() {
      const usrID = this.$fun.getUserID();
      if (!usrID) return;

      _sRequirementService.getUserDetail(usrID, usrID)
        .then(res => {
          if (res && res.data) {
            this.currentUser = res.data;
          }
        })
        .catch(err => {
          console.error("Error loading user details:", err);
        });
    },

    onScopeChange() {
      this.loadDashboardData();
    },

    loadDashboardData() {
      this.loading = true;
      const requestID = this.$fun.getUserID();

      const filterPayload = {};
      if (this.scopeMode === "my") {
        filterPayload.usrID = requestID;
      } else if (this.scopeMode === "gerencia" && this.currentUser.GerenciaID) {
        filterPayload.areParentID = this.currentUser.GerenciaID;
      }
      if (this.filters.project) {
        filterPayload.reqProject = this.filters.project;
      }

      _sRequirementService.getDashboardKPIs(requestID, filterPayload)
        .then(response => {
          if (response.status === 200 && response.data) {
            const data = response.data;
            this.kpis = {
              TotalRequirements: data.TotalRequirements || 0,
              TotalStories: data.TotalStories || 0,
              ActiveRequirements: data.ActiveRequirements || 0,
              ActiveStories: data.ActiveStories || 0,
              BlockedRequirements: data.BlockedRequirements || 0,
              InProduction: data.InProduction || 0,
              ActiveStoryPoints: data.ActiveStoryPoints || 0,
              CompletedStoryPoints: data.CompletedStoryPoints || 0,
              OverdueRequirements: data.OverdueRequirements || 0,
              GlobalProgress: data.GlobalProgress || 0
            };
            this.byStatus = data.ByStatus || [];
            this.byGerencia = data.ByGerencia || [];
            this.byProject = data.ByProject || [];
            this.actionables = data.Actionables || [];

            // Si el backend retornó los requerimientos e historias ya ensamblados (nuevo SP)
            if (data.Requirements && data.Requirements.length > 0) {
              const storiesList = data.Stories || [];
              this.stories = storiesList;

              this.requirements = data.Requirements.map(req => {
                const reqStories = storiesList.filter(s => s.UsoReqID === req.ReqID);
                return {
                  ...req,
                  stories: reqStories
                };
              });

              this.recalculateDashboardMetrics();
            } else {
              // Resiliencia/Fallback: Si el usuario aún no ejecutó el SP nuevo en la base de datos,
              // cargamos los requerimientos e historias por paginación y calculamos el desglose en el frontend
              this.fetchRequirementsAndStoriesFallback();
            }
          }
        })
        .catch(error => {
          console.error("Error loading dashboard data:", error);
          this.fetchRequirementsAndStoriesFallback();
        })
        .finally(() => {
          this.loading = false;
        });
    },

    fetchRequirementsAndStoriesFallback() {
      const requestID = this.$fun.getUserID();
      Promise.all([
        _sRequirementService.pagination({ Length: 1000, Start: 0 }, requestID),
        _sUserStoryService.pagination({ Length: 2000, Start: 0 }, requestID)
      ])
        .then(([reqRes, storyRes]) => {
          const rawReqs = (reqRes.data && reqRes.data.data) || [];
          const rawStories = (storyRes.data && storyRes.data.data) || [];

          this.stories = rawStories.map(s => ({
            ...s,
            StoryProgressPercent: this.getStoryProgressPercent(s.UsoStatus),
            DeviationDays: s.DeviationDays
          }));

          this.requirements = rawReqs.map(r => {
            const reqStories = this.stories.filter(s => s.UsoReqID === r.ReqID);
            const progress = this.calculateRequirementProgress(r, reqStories);
            const doneCount = reqStories.filter(s => s.UsoStatus === 8).length;
            const totalPoints = reqStories.reduce((acc, s) => acc + (s.UsoStoryPoints || 0), 0);
            const completedPoints = reqStories
              .filter(s => s.UsoStatus === 8)
              .reduce((acc, s) => acc + (s.UsoStoryPoints || 0), 0);

            return {
              ReqID: r.ReqID,
              ReqCode: r.ReqCode,
              ReqTitle: r.ReqTitle,
              ReqProject: r.ReqProject,
              ReqDate: r.ReqDate,
              UsrCreateID: r.UsrCreateID,
              RequesterName: r.RequesterName,
              GerenciaName: r.GerenciaName,
              ReqStatus: r.ReqStatus,
              ReqStatusName: r.ReqStatusName,
              ReqInitialPriority: r.ReqInitialPriority,
              ReqInitialPriorityName: r.ReqInitialPriorityName,
              StoryCount: reqStories.length,
              StoriesDoneCount: doneCount,
              TotalStoryPoints: totalPoints,
              CompletedStoryPoints: completedPoints,
              ProgressPercent: progress,
              HasBlockedStories: reqStories.some(s => s.UsoIsBlocked),
              HasOverdueStories: reqStories.some(s => s.DeviationDays < 0 && s.UsoStatus < 8),
              stories: reqStories
            };
          });

          this.recalculateDashboardMetrics();
        })
        .catch(err => {
          console.error("Error in fallback fetch:", err);
        });
    },

    recalculateDashboardMetrics() {
      const reqs = this.filteredRequirements;
      const allStories = [];
      reqs.forEach(r => {
        (r.stories || []).forEach(s => allStories.push(s));
      });

      const totalStories = allStories.length;
      const activeStories = allStories.filter(s => s.UsoStatus >= 2 && s.UsoStatus <= 7).length;
      const inProd = allStories.filter(s => s.UsoStatus === 8).length;
      const activePoints = allStories
        .filter(s => s.UsoStatus >= 2 && s.UsoStatus <= 7)
        .reduce((acc, s) => acc + (s.UsoStoryPoints || 0), 0);
      const completedPoints = allStories
        .filter(s => s.UsoStatus === 8)
        .reduce((acc, s) => acc + (s.UsoStoryPoints || 0), 0);

      const avgProgress = reqs.length > 0
        ? (reqs.reduce((acc, r) => acc + (r.ProgressPercent || 0), 0) / reqs.length).toFixed(1)
        : 0;

      this.kpis.TotalRequirements = reqs.length;
      this.kpis.TotalStories = totalStories;
      this.kpis.ActiveStories = activeStories;
      this.kpis.InProduction = inProd;
      this.kpis.ActiveStoryPoints = activePoints;
      this.kpis.CompletedStoryPoints = completedPoints;
      this.kpis.GlobalProgress = Number(avgProgress);

      // Casos críticos desde historias filtradas
      const critical = [];
      allStories.forEach(s => {
        if (s.UsoIsBlocked || (s.DeviationDays != null && s.DeviationDays < 0 && s.UsoStatus < 8)) {
          const parentReq = reqs.find(r => r.ReqID === s.UsoReqID);
          critical.push({
            ReqCode: parentReq ? parentReq.ReqCode : `REQ-${s.UsoReqID}`,
            ReqTitle: parentReq ? parentReq.ReqTitle : "",
            UsoCode: s.UsoCode,
            UsoTitle: s.UsoTitle,
            GerenciaName: parentReq ? parentReq.GerenciaName : "",
            StatusName: s.UsoStatusName,
            AssignedITName: s.AssignedITName,
            ReqIsBlocked: s.UsoIsBlocked,
            BlockReason: s.UsoBlockReason,
            ReqNextAction: s.UsoNextAction,
            DeviationDays: s.DeviationDays
          });
        }
      });
      this.actionables = critical;
    },

    calculateRequirementProgress(req, reqStories) {
      if (!reqStories || reqStories.length === 0) {
        const baseMap = { 1: 0, 2: 15, 3: 30, 4: 60, 5: 80, 6: 90, 7: 95, 8: 100 };
        return baseMap[req.ReqStatus] || 0;
      }

      const validStories = reqStories.filter(s => s.UsoStatus !== 9);
      if (validStories.length === 0) return 0;

      const totalPts = validStories.reduce((acc, s) => acc + (s.UsoStoryPoints || 0), 0);
      if (totalPts > 0) {
        const weightedSum = validStories.reduce((acc, s) => {
          const p = this.getStoryProgressPercent(s.UsoStatus);
          return acc + ((s.UsoStoryPoints || 0) * p);
        }, 0);
        return Number((weightedSum / totalPts).toFixed(1));
      } else {
        const sum = validStories.reduce((acc, s) => acc + this.getStoryProgressPercent(s.UsoStatus), 0);
        return Number((sum / validStories.length).toFixed(1));
      }
    },

    getStoryProgressPercent(status) {
      switch (status) {
        case 1: return 0;   // Nuevo
        case 2: return 15;  // En Refinamiento
        case 3: return 30;  // Listo para Desarrollo
        case 4: return 60;  // En Desarrollo
        case 5: return 80;  // En QA
        case 6: return 90;  // En UAT
        case 7: return 95;  // Listo para Producción
        case 8: return 100; // En Producción
        default: return 0;
      }
    },

    setQuickFilter(type) {
      this.quickFilter = type;
      this.recalculateDashboardMetrics();
    },

    applyClientFilters() {
      this.recalculateDashboardMetrics();
    },

    toggleExpandAll() {
      if (this.expandedAll) {
        this.expanded = [];
        this.expandedAll = false;
      } else {
        this.expanded = this.filteredRequirements.map(r => r.ReqID);
        this.expandedAll = true;
      }
    },

    calculatePercent(part, total) {
      if (!total || total === 0) return 0;
      return ((part / total) * 100).toFixed(1);
    },

    calculateRatio(stories, reqs) {
      if (!reqs || reqs === 0) return 0;
      return (stories / reqs).toFixed(1);
    },

    formatDate(val) {
      if (!val) return "";
      try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return String(val).substring(0, 10);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
      } catch (e) {
        return String(val).substring(0, 10);
      }
    },

    getStatusColor(status) {
      return statusColor(status);
    },

    getStatusColorByName(name) {
      if (!name) return "grey";
      const s = name.toLowerCase();
      if (s.includes("nuevo")) return "blue";
      if (s.includes("refinamiento")) return "amber darken-2";
      if (s.includes("listo para desarrollo")) return "deep-purple";
      if (s.includes("desarrollo")) return "teal darken-1";
      if (s.includes("qa")) return "cyan darken-2";
      if (s.includes("uat")) return "green darken-1";
      if (s.includes("listo para producción")) return "light-green darken-2";
      if (s.includes("producción")) return "green darken-4";
      if (s.includes("cancelado")) return "red darken-2";
      return "indigo";
    },

    getProgressColor(val) {
      const v = Number(val) || 0;
      if (v >= 100) return "green darken-2";
      if (v >= 70) return "teal darken-1";
      if (v >= 35) return "indigo";
      if (v > 0) return "amber darken-2";
      return "grey";
    },

    getProgressTextColor(val) {
      const v = Number(val) || 0;
      if (v >= 100) return "green--text text--darken-3";
      if (v >= 70) return "teal--text text--darken-2";
      if (v >= 35) return "indigo--text text--darken-2";
      if (v > 0) return "amber--text text--darken-3";
      return "grey--text";
    },

    getPriorityColor(prio) {
      switch (prio) {
        case 1: return "red darken-2";
        case 2: return "orange darken-2";
        case 3: return "blue darken-2";
        case 4: return "grey";
        default: return "grey lighten-1";
      }
    }
  }
};
</script>

<style scoped>
.dashboard-software-dev-view {
  background-color: #f8fafc;
  min-height: 100vh;
}

.border-light {
  border: 1px solid #e2e8f0 !important;
}

.border-light-top {
  border-top: 1px solid #edf2f7;
}

.border-light-bottom {
  border-bottom: 1px solid #edf2f7;
}

.border-rounded {
  border-radius: 8px;
}

.bg-grey-lighten {
  background-color: #f8fafc !important;
}
</style>
